import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  TextField,
  Alert,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  alpha,
} from '@mui/material';
import {
  TuneOutlined as TuneIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  PieChart as PieChartIcon,
  Timeline as AreaChartIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Payments as PaymentsIcon,
  WaterDrop as WaterDropIcon,
  Bolt as BoltIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  LocalGasStation as LocalGasStationIcon,
  Recycling as RecyclingIcon,
  Percent as PercentIcon,
  Co2 as Co2Icon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  Dashboard as DashboardIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDashboardStore } from '../stores/dashboard.store';
import { useOrganizationStore } from '../stores/organization.store';
import { DashboardStatistics, StatisticType } from '../types/dashboard.type';
import StatCard from '../components/dashboard/StatCard';
import ChartCard from '../components/dashboard/ChartCard';
import EsgRecycledWasteByTypeChart from '../components/dashboard/EsgRecycledWasteByTypeChart';
import CarbonAvoidedByStreamChart from '../components/dashboard/CarbonAvoidedByStreamChart';
import CarbonAvoidedByMaterialTable from '../components/dashboard/CarbonAvoidedByMaterialTable';
import CarbonAvoidanceDetailView from '../components/dashboard/CarbonAvoidanceDetailView';
import KPILimitsDialog from '../components/dashboard/KPILimitsDialog';
import { format, isValid, parse, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import KPIProgressCard from '../components/dashboard/KPIProgressCard';
import BranchSiteCard from '../components/dashboard/BranchSiteCard';
import BranchComparisonDialog from '../components/dashboard/BranchComparisonDialog';
import DashboardSkeleton from '../components/dashboard/DashboardSkeleton';
import PageHeader from '../components/PageHeader';
import ScopeEmissionsPanel from '../components/data-entry/ScopeEmissionsPanel';
import { SetupCoachmark } from '../components/selfSetup/SetupCoachmark';
import {
  PAGE_HEADER_TABS_ROW_SX,
  PAGE_HEADER_TABS_SX,
} from '../constants/page-header';
import { useSelfSetupTour } from '../hooks/useSelfSetupTour';
import {
  HELP_GUIDE_CUSTOMIZE,
  parseHelpDashboardGuide,
  type HelpDashboardGuideKind,
} from '../constants/knowledgeBaseTryIt';
import { helpI18n } from '../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';
import { useKPIStore } from '../stores/kpi.store';
import { useEsgDashboardStore } from '../stores/esg-dashboard.store';
import { useCarbonAvoidanceReportStore } from '../stores/carbon-avoidance-report.store';
import {
  ESG_MONTHLY_NON_OPERATIONAL_KEYS,
  type CarbonDashboardScalars,
  type EsgDashboardMonthlyResponse,
} from '../types/esg-dashboard.type';
import {
  DEFAULT_ESG_MONTHLY_METRICS,
  ESG_MONTHLY_SUMMARY_METRIC_ORDER,
  ESG_MONTHLY_UTILITY_METRIC_ORDER,
  EsgMonthlyMetricType,
  isEsgMonthlyChartMetric,
  PICKABLE_ESG_MONTHLY_METRICS,
} from '../types/esg-monthly-metric.type';
import {
  DEFAULT_ESG_OVERVIEW_WIDGETS,
  type EsgOverviewWidgetId,
  PICKABLE_ESG_OVERVIEW_WIDGETS,
} from '../types/esg-overview-widget.type';
import {
  DEFAULT_ESG_BRANCHES_WIDGETS,
  DEFAULT_ESG_CARBON_WIDGETS,
  DEFAULT_ESG_MONTHLY_EXTRA_WIDGETS,
  DEFAULT_ESG_SCOPES_WIDGETS,
  ESG_BRANCHES_WIDGETS,
  ESG_CARBON_WIDGETS,
  ESG_MONTHLY_EXTRA_WIDGETS,
  ESG_SCOPES_WIDGETS,
  type EsgBranchesWidgetId,
  type EsgCarbonWidgetId,
  type EsgMonthlyExtraWidgetId,
  type EsgScopesWidgetId,
} from '../types/dashboard-customize.type';
import {
  expandLegacyScopeEmissions,
  loadOperationalStatsForOrg,
  persistOperationalStatsForOrg,
  sanitizePickableIds,
} from '../utils/dashboardCustomizePreferences';
import DashboardCustomizeWidgetList from '../components/dashboard/DashboardCustomizeWidgetList';
import {
  getOperationalStatsForOrgType,
  isOperationalChartStat,
} from '../constants/dashboardStats.catalog';
import {
  getEsgMonthlyMetricLabel,
  getOverviewWidgetLabel,
  getStatisticLabel,
} from '../helpers/metric-i18n.helper';
import {
  carbonReportPeriodToParams,
  formatReportPeriodLabel,
  isCarbonReportPeriodValid,
  pickTotalAvoidedTco2e,
  readCarbonReportPeriodFromSession,
  type CarbonReportPeriod,
  writeCarbonReportPeriodToSession,
} from '../utils/carbonAvoidance.util';

function parseDashboardMonth(iso: string): Date {
  if (!iso) return startOfMonth(new Date());
  if (/^\d{4}-\d{2}$/.test(iso)) {
    const d = parse(`${iso}-01`, 'yyyy-MM-dd', new Date());
    return isValid(d) ? startOfMonth(d) : startOfMonth(new Date());
  }
  const d = parseISO(iso);
  return isValid(d) ? startOfMonth(d) : startOfMonth(new Date());
}

const OVERVIEW_SUMMARY_WIDGETS: EsgOverviewWidgetId[] = [
  'summary_waste_diverted',
  'summary_fill_reports',
  'summary_diversion_rate',
  'summary_total_co2',
  'summary_training_hours',
];

const OVERVIEW_UTILITY_WIDGETS: EsgOverviewWidgetId[] = [
  'utility_water',
  'utility_electricity',
  'utility_oil',
  'utility_gas',
];

enum DashboardSection {
  MONTHLY = 0,
  OVERVIEW = 1,
  SCOPES = 2,
  BRANCHES = 3,
  CARBON_AVOIDANCE = 4,
}

/** Map legacy session keys (split utilities / prior-month widgets) to current picklist. */
const LEGACY_ESG_MONTHLY_METRIC_KEY: Record<string, EsgMonthlyMetricType> = {
  water_consumption: EsgMonthlyMetricType.WATER_USE,
  water_amount_sar: EsgMonthlyMetricType.WATER_USE,
  electricity_consumption_kwh: EsgMonthlyMetricType.ELECTRICITY_USE,
  electricity_amount_sar: EsgMonthlyMetricType.ELECTRICITY_USE,
  oil_consumption_liters: EsgMonthlyMetricType.OIL_USE,
  oil_amount_sar: EsgMonthlyMetricType.OIL_USE,
  gas_consumption_m3: EsgMonthlyMetricType.GAS_USE,
  gas_amount_sar: EsgMonthlyMetricType.GAS_USE,
};

function calcMomPercent(
  current: number,
  previous: number | null | undefined
): number {
  if (previous === null || previous === undefined || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

/** Month-over-month % for ESG monthly StatCards (pairs from ESG_DASHBOARD_API.md). */
function esgMonthlyMetricMoM(
  esg: EsgDashboardMonthlyResponse,
  metric: EsgMonthlyMetricType
): { trend: number; hideTrend: boolean } {
  if (isEsgMonthlyChartMetric(metric)) {
    return { trend: 0, hideTrend: true };
  }

  switch (metric) {
    case EsgMonthlyMetricType.WASTE_DIVERSION_RATE:
      return {
        trend: calcMomPercent(
          esg.waste_diversion_rate_percent,
          esg.previous_month_waste_diversion_rate
        ),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.TOTAL_CO2:
      return { trend: 0, hideTrend: true };
    case EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED:
      return { trend: 0, hideTrend: false };
    case EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT:
      return {
        trend: calcMomPercent(
          esg.fill_level_report_count,
          esg.previous_month_fill_level_report_count
        ),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.TRAINING_HOURS_TOTAL: {
      if (esg.training_hours_total === null) return { trend: 0, hideTrend: true };
      return {
        trend: calcMomPercent(
          esg.training_hours_total,
          esg.previous_month_training_hours_total
        ),
        hideTrend: false,
      };
    }
    case EsgMonthlyMetricType.WATER_USE:
      return {
        trend: calcMomPercent(esg.water_consumption, esg.previous_month_water_consumption),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.ELECTRICITY_USE:
      return {
        trend: calcMomPercent(
          esg.electricity_consumption_kwh,
          esg.previous_month_electricity_consumption_kwh
        ),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.OIL_USE:
      return {
        trend: calcMomPercent(
          esg.oil_consumption_liters,
          esg.previous_month_oil_consumption_liters
        ),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.GAS_USE:
      return {
        trend: calcMomPercent(esg.gas_consumption_m3, esg.previous_month_gas_consumption_m3),
        hideTrend: false,
      };
    default:
      return { trend: 0, hideTrend: false };
  }
}

/**
 * Everything a carbon total leaves out, phrased so the number can explain itself.
 *
 * Skipped runs were the only thing surfaced before, which made a zero total look the
 * same whether nothing was collected or every calculation was failing for want of an
 * emission factor. `missing` is usually the big one: completed pickups that produced
 * no calculation at all.
 */
function uncountedPickupsNote(
  source: Partial<CarbonDashboardScalars>,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  const skipped = source.calculation_skipped_count ?? 0;
  const failed = source.calculation_failed_count ?? 0;
  const missing = source.calculation_missing_count ?? 0;
  const total = skipped + failed + missing;
  if (total === 0) return null;

  const parts: string[] = [];
  if (missing > 0) parts.push(t('dashboard.alerts.uncountedNoCalculation', { count: missing }));
  if (failed > 0) parts.push(t('dashboard.alerts.uncountedFailed', { count: failed }));
  if (skipped > 0) parts.push(t('dashboard.alerts.uncountedSkipped', { count: skipped }));

  const topWarning = source.calculation_warnings?.[0];
  const breakdown = parts.join(', ');
  return topWarning
    ? t('dashboard.alerts.ordersNotCountedWithReason', {
        count: total,
        breakdown,
        reason: topWarning.warning_code,
      })
    : t('dashboard.alerts.ordersNotCounted', { count: total, breakdown });
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { isStep, completeStep, dismissTour, setTourPhase, tourPhase } =
    useSelfSetupTour();
  const showDashboardTour = isStep('dashboard');
  const [helpCustomizeGuide, setHelpCustomizeGuide] = useState(false);
  const [helpDashGuide, setHelpDashGuide] =
    useState<HelpDashboardGuideKind | null>(null);
  /** Self-setup page tips start after the sidebar nav tip. */
  const showOverviewTour =
    (showDashboardTour && tourPhase !== 'nav') || helpDashGuide != null;
  /** Knowledge-base customize guide only (opens the customize dialog). */
  const activeCustomizeGuide = helpCustomizeGuide;
  type OverviewTourPhase =
    | 'monthlyTab'
    | 'month'
    | 'customize'
    | 'customizeSearch'
    | 'customizeMetrics'
    | 'customizeSave'
    | 'kpi'
    | 'kpiForm'
    | 'kpiList'
    | 'esgCards'
    | 'charts'
    | 'branchesTab'
    | 'branches'
    | 'compareBranches'
    | 'compareDialog'
    | 'compareResults'
    | 'carbonTab'
    | 'carbonDate'
    | 'carbonContent';
  type CustomizeDialogPhase = 'button' | 'search' | 'metrics' | 'save';
  const [overviewTourPhase, setOverviewTourPhase] =
    useState<OverviewTourPhase>('monthlyTab');
  const [customizeTourPhase, setCustomizeTourPhase] =
    useState<CustomizeDialogPhase>('button');
  const { selectedOrganization } = useOrganizationStore();
  const orgType = selectedOrganization?.organization_type ?? null;
  const operationalStatsForOrg = useMemo(
    () => (orgType ? getOperationalStatsForOrgType(orgType) : []),
    [orgType],
  );
  const {
    selectedStats,
    selectedMonth,
    setSelectedStats,
    setSelectedMonth,
    loadDashboardPreferences,
  } = useDashboardStore();
  const {
    monthly: esgMonthly,
    overview: esgOverview,
    byBranch: esgByBranch,
    isLoading: esgLoading,
    error: esgError,
    fetchEsgDashboard,
  } = useEsgDashboardStore();
  const {
    report: carbonReport,
    isLoading: carbonLoading,
    error: carbonError,
    fetchCarbonAvoidanceReport,
    reset: resetCarbonReport,
  } = useCarbonAvoidanceReportStore();

  const { limits } = useKPIStore();
  const hasEnabledKPIs = limits.some((limit) => limit.isEnabled);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStat, setSelectedStat] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openKPIDialog, setOpenKPIDialog] = useState(false);
  const [openBranchCompare, setOpenBranchCompare] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [chartTypes, setChartTypes] = useState<Record<string, 'bar' | 'line' | 'area' | 'pie'>>({});
  const [dashboardTab, setDashboardTab] = useState<DashboardSection>(
    DashboardSection.MONTHLY
  );
  const [selectedEsgMonthlyMetrics, setSelectedEsgMonthlyMetrics] = useState<EsgMonthlyMetricType[]>(
    DEFAULT_ESG_MONTHLY_METRICS
  );
  const [selectedOverviewWidgets, setSelectedOverviewWidgets] = useState<EsgOverviewWidgetId[]>(
    DEFAULT_ESG_OVERVIEW_WIDGETS
  );
  const [selectedMonthlyExtras, setSelectedMonthlyExtras] = useState<EsgMonthlyExtraWidgetId[]>(
    DEFAULT_ESG_MONTHLY_EXTRA_WIDGETS
  );
  const [selectedScopesWidgets, setSelectedScopesWidgets] = useState<EsgScopesWidgetId[]>(
    DEFAULT_ESG_SCOPES_WIDGETS
  );
  const [selectedBranchesWidgets, setSelectedBranchesWidgets] = useState<EsgBranchesWidgetId[]>(
    DEFAULT_ESG_BRANCHES_WIDGETS
  );
  const [selectedCarbonWidgets, setSelectedCarbonWidgets] = useState<EsgCarbonWidgetId[]>(
    DEFAULT_ESG_CARBON_WIDGETS
  );
  const [carbonPeriod, setCarbonPeriod] = useState<CarbonReportPeriod>(() =>
    readCarbonReportPeriodFromSession()
  );

  const showMonthlyTabTip = showOverviewTour && overviewTourPhase === 'monthlyTab';
  const showMonthTip = showOverviewTour && overviewTourPhase === 'month';
  const showKpiTip = showOverviewTour && overviewTourPhase === 'kpi';
  const showOverviewCustomizeTip =
    showOverviewTour && overviewTourPhase === 'customize';
  const showEsgCardsTip = showOverviewTour && overviewTourPhase === 'esgCards';
  const showChartsTip = showOverviewTour && overviewTourPhase === 'charts';
  const showBranchesTabTip =
    showOverviewTour && overviewTourPhase === 'branchesTab';
  const showBranchesTip = showOverviewTour && overviewTourPhase === 'branches';
  const showCompareTip =
    showOverviewTour && overviewTourPhase === 'compareBranches';
  const showCompareDialogTip =
    showOverviewTour && overviewTourPhase === 'compareDialog';
  const showCompareResultsTip =
    showOverviewTour && overviewTourPhase === 'compareResults';
  const showCompareTourDialog =
    showCompareDialogTip || showCompareResultsTip;
  const showCarbonTabTip = showOverviewTour && overviewTourPhase === 'carbonTab';
  const showCarbonDateTip =
    showOverviewTour && overviewTourPhase === 'carbonDate';
  const showCarbonContentTip =
    showOverviewTour && overviewTourPhase === 'carbonContent';

  const showCustomizeButtonTip =
    (activeCustomizeGuide && customizeTourPhase === 'button') ||
    showOverviewCustomizeTip;
  const showCustomizeSearchTip =
    (activeCustomizeGuide && customizeTourPhase === 'search') ||
    (showOverviewTour && overviewTourPhase === 'customizeSearch');
  const showCustomizeMetricsTip =
    (activeCustomizeGuide && customizeTourPhase === 'metrics') ||
    (showOverviewTour && overviewTourPhase === 'customizeMetrics');
  const showCustomizeSaveTip =
    (activeCustomizeGuide && customizeTourPhase === 'save') ||
    (showOverviewTour && overviewTourPhase === 'customizeSave');
  const showKpiFormTip = showOverviewTour && overviewTourPhase === 'kpiForm';
  const showKpiListTip = showOverviewTour && overviewTourPhase === 'kpiList';

  const syncPhase = (phase: OverviewTourPhase) => {
    setOverviewTourPhase(phase);
    if (showDashboardTour) setTourPhase(phase);
  };

  const finishDashboardGuide = () => {
    setOpenDialog(false);
    setOpenKPIDialog(false);
    setOpenBranchCompare(false);
    setOverviewTourPhase('monthlyTab');
    setCustomizeTourPhase('button');
    if (helpCustomizeGuide) {
      setHelpCustomizeGuide(false);
      clearHelpGuideActive();
      return;
    }
    if (helpDashGuide) {
      setHelpDashGuide(null);
      clearHelpGuideActive();
      if (showDashboardTour) {
        completeStep('dashboard');
      }
      return;
    }
    completeStep('dashboard');
  };

  /** Soft-dismiss (X): Later for self-setup — do not mark the step done. */
  const softDismissDashboardTour = () => {
    setOpenDialog(false);
    setOpenKPIDialog(false);
    setOpenBranchCompare(false);
    if (helpCustomizeGuide) {
      setHelpCustomizeGuide(false);
      clearHelpGuideActive();
      setCustomizeTourPhase('button');
      return;
    }
    if (helpDashGuide) {
      setHelpDashGuide(null);
      clearHelpGuideActive();
      setOverviewTourPhase('monthlyTab');
      return;
    }
    dismissTour(overviewTourPhase);
  };

  // Resume self-setup tip from stored spot after Continue / re-login.
  useEffect(() => {
    if (!showDashboardTour) return;
    if (tourPhase === 'nav') return;
    const phase = tourPhase;
    const known: OverviewTourPhase[] = [
      'monthlyTab',
      'month',
      'customize',
      'customizeSearch',
      'customizeMetrics',
      'customizeSave',
      'kpi',
      'kpiForm',
      'kpiList',
      'esgCards',
      'charts',
      'branchesTab',
      'branches',
      'compareBranches',
      'compareDialog',
      'compareResults',
      'carbonTab',
      'carbonDate',
      'carbonContent',
    ];
    if (known.includes(phase as OverviewTourPhase)) {
      setOverviewTourPhase(phase as OverviewTourPhase);
      if (
        phase === 'branchesTab' ||
        phase === 'branches' ||
        phase === 'compareBranches' ||
        phase === 'compareDialog' ||
        phase === 'compareResults'
      ) {
        setDashboardTab(DashboardSection.BRANCHES);
      } else if (
        phase === 'carbonTab' ||
        phase === 'carbonDate' ||
        phase === 'carbonContent'
      ) {
        setDashboardTab(DashboardSection.CARBON_AVOIDANCE);
      } else {
        setDashboardTab(DashboardSection.MONTHLY);
      }
    }
  }, [showDashboardTour, tourPhase, setTourPhase]);

  const openCustomizeDialogFromTour = () => {
    setOpenDialog(true);
    if (activeCustomizeGuide) {
      setCustomizeTourPhase('search');
    } else if (showOverviewTour) {
      syncPhase('customizeSearch');
    }
  };

  const advanceOverviewTour = () => {
    if (overviewTourPhase === 'monthlyTab') {
      setDashboardTab(DashboardSection.MONTHLY);
      syncPhase('month');
      return;
    }
    if (overviewTourPhase === 'month') {
      syncPhase('customize');
      return;
    }
    if (overviewTourPhase === 'customize') {
      setOpenDialog(true);
      syncPhase('customizeSearch');
      return;
    }
    if (overviewTourPhase === 'customizeSearch') {
      syncPhase('customizeMetrics');
      return;
    }
    if (overviewTourPhase === 'customizeMetrics') {
      syncPhase('customizeSave');
      return;
    }
    if (overviewTourPhase === 'customizeSave') {
      setOpenDialog(false);
      syncPhase('kpi');
      return;
    }
    if (overviewTourPhase === 'kpi') {
      setOpenKPIDialog(true);
      syncPhase('kpiForm');
      return;
    }
    if (overviewTourPhase === 'kpiForm') {
      syncPhase('kpiList');
      return;
    }
    if (overviewTourPhase === 'kpiList') {
      if (helpDashGuide === 'kpi') {
        finishDashboardGuide();
        return;
      }
      setOpenKPIDialog(false);
      setDashboardTab(DashboardSection.MONTHLY);
      syncPhase('esgCards');
      return;
    }
    if (overviewTourPhase === 'esgCards') {
      syncPhase('charts');
      return;
    }
    if (overviewTourPhase === 'charts') {
      setDashboardTab(DashboardSection.BRANCHES);
      syncPhase('branchesTab');
      return;
    }
    if (overviewTourPhase === 'branchesTab') {
      setDashboardTab(DashboardSection.BRANCHES);
      syncPhase('branches');
      return;
    }
    if (overviewTourPhase === 'branches') {
      syncPhase('compareBranches');
      return;
    }
    if (overviewTourPhase === 'compareBranches') {
      setOpenBranchCompare(true);
      syncPhase('compareDialog');
      return;
    }
    if (overviewTourPhase === 'compareDialog') {
      syncPhase('compareResults');
      return;
    }
    if (overviewTourPhase === 'compareResults') {
      if (helpDashGuide === 'branches') {
        finishDashboardGuide();
        return;
      }
      setOpenBranchCompare(false);
      setDashboardTab(DashboardSection.CARBON_AVOIDANCE);
      syncPhase('carbonTab');
      return;
    }
    if (overviewTourPhase === 'carbonTab') {
      setDashboardTab(DashboardSection.CARBON_AVOIDANCE);
      syncPhase('carbonDate');
      return;
    }
    if (overviewTourPhase === 'carbonDate') {
      syncPhase('carbonContent');
      return;
    }
    finishDashboardGuide();
  };

  const advanceDashboardGuide = () => {
    if (showOverviewTour && !activeCustomizeGuide) {
      advanceOverviewTour();
      return;
    }
    if (!activeCustomizeGuide) {
      finishDashboardGuide();
      return;
    }
    if (customizeTourPhase === 'button') {
      openCustomizeDialogFromTour();
      return;
    }
    if (customizeTourPhase === 'search') {
      setCustomizeTourPhase('metrics');
      return;
    }
    if (customizeTourPhase === 'metrics') {
      setCustomizeTourPhase('save');
      return;
    }
    finishDashboardGuide();
  };

  useEffect(() => {
    if (!activeCustomizeGuide) {
      setCustomizeTourPhase('button');
    }
  }, [activeCustomizeGuide]);

  useEffect(() => {
    if (
      (activeCustomizeGuide &&
        (customizeTourPhase === 'search' ||
          customizeTourPhase === 'metrics' ||
          customizeTourPhase === 'save')) ||
      overviewTourPhase === 'customizeSearch' ||
      overviewTourPhase === 'customizeMetrics' ||
      overviewTourPhase === 'customizeSave'
    ) {
      setOpenDialog(true);
    }
  }, [activeCustomizeGuide, customizeTourPhase, overviewTourPhase]);

  useEffect(() => {
    if (overviewTourPhase === 'kpiForm' || overviewTourPhase === 'kpiList') {
      setOpenKPIDialog(true);
    }
  }, [overviewTourPhase]);

  useEffect(() => {
    if (overviewTourPhase === 'compareDialog' || overviewTourPhase === 'compareResults') {
      setOpenBranchCompare(true);
    }
  }, [overviewTourPhase]);

  useEffect(() => {
    const helpGuide = new URLSearchParams(location.search).get('helpGuide');
    if (!helpGuide) return;

    // Leave language/appearance for Layout.
    if (helpGuide === HELP_GUIDE_CUSTOMIZE) {
      markHelpGuideActive(helpGuide);
      setHelpCustomizeGuide(true);
      setCustomizeTourPhase('button');
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }

    const dashGuide = parseHelpDashboardGuide(helpGuide);
    if (!dashGuide) return;

    markHelpGuideActive(helpGuide);
    setHelpDashGuide(dashGuide);
    if (dashGuide === 'overview') {
      setOverviewTourPhase('monthlyTab');
      setDashboardTab(DashboardSection.MONTHLY);
    } else if (dashGuide === 'kpi') {
      setOverviewTourPhase('kpi');
      setDashboardTab(DashboardSection.MONTHLY);
    } else if (dashGuide === 'branches') {
      setOverviewTourPhase('branchesTab');
      setDashboardTab(DashboardSection.BRANCHES);
    } else {
      setOverviewTourPhase('carbonTab');
      setDashboardTab(DashboardSection.CARBON_AVOIDANCE);
    }

    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!activeCustomizeGuide && !showOverviewTour) return;
    window.requestAnimationFrame(() => {
      const idMap: Partial<Record<OverviewTourPhase, string>> = {
        monthlyTab: 'self-setup-dashboard-tab-monthly',
        month: 'self-setup-dashboard-month',
        customize: 'self-setup-dashboard-customize',
        customizeSearch: 'self-setup-dashboard-search',
        customizeMetrics: 'self-setup-dashboard-metrics',
        customizeSave: 'self-setup-dashboard-save',
        kpi: 'self-setup-dashboard-kpi',
        kpiForm: 'self-setup-kpi-form',
        kpiList: 'self-setup-kpi-list',
        esgCards: 'self-setup-dashboard-esg',
        charts: 'self-setup-dashboard-charts',
        branchesTab: 'self-setup-dashboard-tab-branches',
        branches: 'self-setup-dashboard-branches',
        compareBranches: 'self-setup-dashboard-compare',
        compareDialog: 'self-setup-dashboard-compare-dialog',
        compareResults: 'self-setup-dashboard-compare-results',
        carbonTab: 'self-setup-dashboard-tab-carbon',
        carbonDate: 'self-setup-dashboard-carbon-date',
        carbonContent: 'self-setup-dashboard-carbon-content',
      };
      const id = showOverviewTour
        ? idMap[overviewTourPhase] ?? 'self-setup-dashboard-tabs'
        : customizeTourPhase === 'search'
          ? 'self-setup-dashboard-search'
          : customizeTourPhase === 'metrics'
            ? 'self-setup-dashboard-metrics'
            : customizeTourPhase === 'save'
              ? 'self-setup-dashboard-save'
              : 'self-setup-dashboard-customize';
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [
    activeCustomizeGuide,
    showOverviewTour,
    overviewTourPhase,
    customizeTourPhase,
  ]);
  const carbonToday = useMemo(() => startOfDay(new Date()), []);

  const selectedOverviewSet = useMemo(
    () => new Set(selectedOverviewWidgets),
    [selectedOverviewWidgets]
  );

  const selectedMonthDate = useMemo(() => parseDashboardMonth(selectedMonth), [selectedMonth]);

  const carbonPeriodValid = isCarbonReportPeriodValid(carbonPeriod);
  const carbonPeriodLabel = useMemo(
    () =>
      formatReportPeriodLabel(
        format(carbonPeriod.startDate, 'yyyy-MM-dd'),
        format(carbonPeriod.endDate, 'yyyy-MM-dd')
      ),
    [carbonPeriod]
  );

  const operationalDashboard = esgMonthly
    ? (esgMonthly as unknown as DashboardStatistics)
    : null;

  const esgMonthlyChartMetrics = useMemo(
    () => selectedEsgMonthlyMetrics.filter((m) => isEsgMonthlyChartMetric(m)),
    [selectedEsgMonthlyMetrics]
  );

  const operationalChartStats = useMemo(() => {
    if (!operationalDashboard) return [] as StatisticType[];
    return selectedStats.filter((s) => {
      if (!operationalStatsForOrg.includes(s) || !isOperationalChartStat(s)) return false;
      const v = operationalDashboard[s as keyof DashboardStatistics];
      return Array.isArray(v);
    });
  }, [selectedStats, operationalDashboard, operationalStatsForOrg]);

  const hasMonthlyCharts =
    esgMonthlyChartMetrics.length > 0 || operationalChartStats.length > 0;

  const hasNoOperationalData = (() => {
    if (!esgMonthly) return true;
    const stripped: Record<string, unknown> = { ...esgMonthly };
    ESG_MONTHLY_NON_OPERATIONAL_KEYS.forEach((k) => {
      delete stripped[k];
    });
    return Object.values(stripped).every(
      (value) =>
        value === 0 ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
    );
  })();

  const orderedMonthlyNumberCards = useMemo(() => {
    const selected = new Set(selectedEsgMonthlyMetrics);
    const row: Array<
      | { kind: 'esg'; metric: EsgMonthlyMetricType }
      | { kind: 'ops'; stat: StatisticType }
    > = [];

    for (const m of ESG_MONTHLY_SUMMARY_METRIC_ORDER) {
      if (selected.has(m) && !isEsgMonthlyChartMetric(m)) {
        row.push({ kind: 'esg', metric: m });
      }
    }

    const showAvgPrice =
      !hasNoOperationalData &&
      selectedStats.includes(StatisticType.AVERAGE_INCENTIVE_PER_KG) &&
      operationalStatsForOrg.includes(StatisticType.AVERAGE_INCENTIVE_PER_KG) &&
      operationalDashboard &&
      typeof operationalDashboard.average_incentive_per_kg === 'number';
    if (showAvgPrice) {
      row.push({ kind: 'ops', stat: StatisticType.AVERAGE_INCENTIVE_PER_KG });
    }

    for (const m of ESG_MONTHLY_UTILITY_METRIC_ORDER) {
      if (selected.has(m) && !isEsgMonthlyChartMetric(m)) {
        row.push({ kind: 'esg', metric: m });
      }
    }

    for (const stat of operationalStatsForOrg) {
      if (!selectedStats.includes(stat) || isOperationalChartStat(stat)) continue;
      if (stat === StatisticType.AVERAGE_INCENTIVE_PER_KG) continue;
      row.push({ kind: 'ops', stat });
    }

    return row;
  }, [
    selectedEsgMonthlyMetrics,
    selectedStats,
    operationalDashboard,
    operationalStatsForOrg,
    hasNoOperationalData,
  ]);

  const esgBranchRows = esgByBranch
    ? [
        ...esgByBranch.branches,
        ...(esgByBranch.unassigned_waste ? [esgByBranch.unassigned_waste] : []),
      ]
    : [];

  const kpiLimitedKeys = limits
    .filter((l) => l.isEnabled)
    .map((l) => l.name.toLowerCase());

  const getDisplayTitle = (statKey: string): string => getStatisticLabel(t, statKey);

  const getEsgMetricTitle = (metric: EsgMonthlyMetricType): string =>
    getEsgMonthlyMetricLabel(t, metric);

  const customizeDialogCopy =
    dashboardTab === DashboardSection.MONTHLY
      ? { title: t('dashboard.customizeMonthly'), hint: t('dashboard.customizeMonthlyHint') }
      : dashboardTab === DashboardSection.OVERVIEW
        ? { title: t('dashboard.customizeOverview'), hint: t('dashboard.customizeOverviewHint') }
        : dashboardTab === DashboardSection.SCOPES
          ? { title: t('dashboard.customizeScopes'), hint: t('dashboard.customizeScopesHint') }
          : dashboardTab === DashboardSection.BRANCHES
            ? { title: t('dashboard.customizeBranches'), hint: t('dashboard.customizeBranchesHint') }
            : { title: t('dashboard.customizeCarbon'), hint: t('dashboard.customizeCarbonHint') };

  const monthlyCustomizeItems = [
    ...ESG_MONTHLY_EXTRA_WIDGETS.map((id) => ({
      id,
      label: t(`dashboard.monthlyExtras.${id}`),
    })),
    ...PICKABLE_ESG_MONTHLY_METRICS.map((id) => ({
      id,
      label: getEsgMetricTitle(id),
    })),
    ...operationalStatsForOrg.map((id) => ({
      id,
      label: getDisplayTitle(id),
    })),
  ];
  const monthlyCustomizeSelected = [
    ...selectedMonthlyExtras,
    ...selectedEsgMonthlyMetrics,
    ...selectedStats.filter((s) => operationalStatsForOrg.includes(s)),
  ];

  const scopesCustomizeItems = ESG_SCOPES_WIDGETS.map((id) => ({
    id,
    label: t(`dashboard.scopeWidgets.${id}`),
  }));
  const branchesCustomizeItems = ESG_BRANCHES_WIDGETS.map((id) => ({
    id,
    label: t(`dashboard.branchWidgets.${id}`),
  }));
  const carbonCustomizeItems = ESG_CARBON_WIDGETS.map((id) => ({
    id,
    label: t(`dashboard.carbonWidgets.${id}`),
  }));

  const formatOverviewSar = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'SAR' });

  useEffect(() => {
    loadDashboardPreferences();
    
    // Get selected month from sessionStorage or use current month
    const storedMonth = sessionStorage.getItem('selectedMonth');
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd'T'HH:mm:ss'Z'");
    
    const monthToUse = storedMonth || formattedDate;
    setSelectedMonth(monthToUse);
    sessionStorage.setItem('selectedMonth', monthToUse);
    
    // Load operational picks for the current organization type.
    if (orgType) {
      const next = loadOperationalStatsForOrg(orgType);
      setSelectedStats(next);
      persistOperationalStatsForOrg(orgType, next);
    } else {
      setSelectedStats([]);
    }

    const storedEsgMetrics = sessionStorage.getItem('esgMonthlySelectedMetrics');
    if (storedEsgMetrics) {
      try {
        const parsed = JSON.parse(storedEsgMetrics) as string[];
        const mapped = parsed.map((m) => LEGACY_ESG_MONTHLY_METRIC_KEY[m] ?? m);
        const migrated = sanitizePickableIds(
          mapped,
          PICKABLE_ESG_MONTHLY_METRICS,
          [
            EsgMonthlyMetricType.WATER_USE,
            EsgMonthlyMetricType.ELECTRICITY_USE,
            EsgMonthlyMetricType.OIL_USE,
            EsgMonthlyMetricType.GAS_USE,
          ],
        );
        setSelectedEsgMonthlyMetrics(migrated);
        sessionStorage.setItem('esgMonthlySelectedMetrics', JSON.stringify(migrated));
      } catch {
        /* ignore invalid */
      }
    }

    const storedMonthlyExtras = sessionStorage.getItem('esgMonthlySelectedExtras');
    if (storedMonthlyExtras) {
      try {
        const parsed = JSON.parse(storedMonthlyExtras) as unknown;
        const next = sanitizePickableIds(
          expandLegacyScopeEmissions(parsed),
          ESG_MONTHLY_EXTRA_WIDGETS,
        );
        setSelectedMonthlyExtras(next);
        sessionStorage.setItem('esgMonthlySelectedExtras', JSON.stringify(next));
      } catch {
        /* ignore invalid */
      }
    }

    const storedOverviewWidgets = sessionStorage.getItem('esgOverviewSelectedWidgets');
    if (storedOverviewWidgets) {
      try {
        const parsed = JSON.parse(storedOverviewWidgets) as string[];
        const next = sanitizePickableIds(
          expandLegacyScopeEmissions(parsed),
          PICKABLE_ESG_OVERVIEW_WIDGETS,
          [
            'scope_1',
            'scope_2',
            'utility_water',
            'utility_electricity',
            'utility_oil',
            'utility_gas',
          ],
        );
        setSelectedOverviewWidgets(next);
        sessionStorage.setItem('esgOverviewSelectedWidgets', JSON.stringify(next));
      } catch {
        /* ignore invalid */
      }
    }

    const storedScopes = sessionStorage.getItem('esgScopesSelectedWidgets');
    if (storedScopes) {
      try {
        setSelectedScopesWidgets(
          sanitizePickableIds(JSON.parse(storedScopes), ESG_SCOPES_WIDGETS),
        );
      } catch {
        /* ignore invalid */
      }
    }

    const storedBranches = sessionStorage.getItem('esgBranchesSelectedWidgets');
    if (storedBranches) {
      try {
        setSelectedBranchesWidgets(
          sanitizePickableIds(JSON.parse(storedBranches), ESG_BRANCHES_WIDGETS),
        );
      } catch {
        /* ignore invalid */
      }
    }

    const storedCarbon = sessionStorage.getItem('esgCarbonSelectedWidgets');
    if (storedCarbon) {
      try {
        setSelectedCarbonWidgets(
          sanitizePickableIds(JSON.parse(storedCarbon), ESG_CARBON_WIDGETS),
        );
      } catch {
        /* ignore invalid */
      }
    }

    if (selectedOrganization?.id && selectedOrganization?.organization_type) {
      fetchEsgDashboard(
        selectedOrganization.id,
        selectedOrganization.organization_type,
        monthToUse
      );
    }
  }, [selectedOrganization?.id, selectedOrganization?.organization_type, fetchEsgDashboard]);

  useEffect(() => {
    if (!selectedOrganization?.id || !carbonPeriodValid) {
      resetCarbonReport();
      return;
    }
    fetchCarbonAvoidanceReport(
      selectedOrganization.id,
      carbonReportPeriodToParams(carbonPeriod)
    );
  }, [
    selectedOrganization?.id,
    carbonPeriod,
    carbonPeriodValid,
    fetchCarbonAvoidanceReport,
    resetCarbonReport,
  ]);

  const updateCarbonPeriod = (patch: Partial<CarbonReportPeriod>) => {
    setCarbonPeriod((prev) => {
      const next = { ...prev, ...patch };
      writeCarbonReportPeriodToSession(next);
      return next;
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, statId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedStat(statId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStat(null);
  };

  const handleChartTypeChange = (type: 'bar' | 'line' | 'area' | 'pie') => {
    if (selectedStat) {
      setChartTypes(prev => ({ ...prev, [selectedStat]: type }));
      handleMenuClose();
    }
  };

  const handleOpenDialog = () => {
    setSearchQuery('');
    setOpenDialog(true);
    if (activeCustomizeGuide) {
      setCustomizeTourPhase('search');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    if (
      activeCustomizeGuide &&
      (customizeTourPhase === 'search' ||
        customizeTourPhase === 'metrics' ||
        customizeTourPhase === 'save')
    ) {
      // Keep tour active on the button if they close without finishing
      setCustomizeTourPhase('button');
    }
  };

  const handleSavePreferences = () => {
    if (dashboardTab === DashboardSection.MONTHLY) {
      const ops = selectedStats.filter((s) => operationalStatsForOrg.includes(s));
      setSelectedStats(ops);
      if (orgType) persistOperationalStatsForOrg(orgType, ops);
      sessionStorage.setItem('esgMonthlySelectedMetrics', JSON.stringify(selectedEsgMonthlyMetrics));
      sessionStorage.setItem('esgMonthlySelectedExtras', JSON.stringify(selectedMonthlyExtras));
    } else if (dashboardTab === DashboardSection.OVERVIEW) {
      sessionStorage.setItem('esgOverviewSelectedWidgets', JSON.stringify(selectedOverviewWidgets));
    } else if (dashboardTab === DashboardSection.SCOPES) {
      sessionStorage.setItem('esgScopesSelectedWidgets', JSON.stringify(selectedScopesWidgets));
    } else if (dashboardTab === DashboardSection.BRANCHES) {
      sessionStorage.setItem('esgBranchesSelectedWidgets', JSON.stringify(selectedBranchesWidgets));
    } else if (dashboardTab === DashboardSection.CARBON_AVOIDANCE) {
      sessionStorage.setItem('esgCarbonSelectedWidgets', JSON.stringify(selectedCarbonWidgets));
    }
    if (activeCustomizeGuide) {
      finishDashboardGuide();
    } else {
      handleCloseDialog();
    }
  };

  const handleOpenKPIDialog = () => {
    setOpenKPIDialog(true);
  };

  const handleCloseKPIDialog = () => {
    setOpenKPIDialog(false);
  };

  const handleMonthChange = (date: string) => {
    setSelectedMonth(date);
    sessionStorage.setItem('selectedMonth', date);
    if (selectedOrganization?.id && selectedOrganization?.organization_type) {
      fetchEsgDashboard(
        selectedOrganization.id,
        selectedOrganization.organization_type,
        date
      );
    }
  };

  const KPI_UNITS: Record<string, string> = {
    // orders
    total_orders: "orders",
    pending_orders: "orders",
    completed_orders: "orders",
    cancelled_orders: "orders",

    // revenue & incentives
    total_revenue: "SAR",
    average_incentive_per_kg: "SAR/kg",

    // percentages
    full_percentage: "%",
    completion_rate: "%",
    cancellation_rate: "%",
    waste_diversion_rate: "%",
    quality_compliance_rate: "%",
    rejected_waste_percentage: "%",
    storage_capacity_utilization: "%",
    processing_capacity_utilization: "%",

    // weights
    total_waste_amount: "kg",
    completed_waste_amount: "kg",
    sustainable_supply: "kg",

    // time
    average_processing_time_hours: "hours",
  };

  const getUnitForStat = (stat: StatisticType): string => {
    switch (stat) {
      case StatisticType.TIME_FROM_FULL_TO_COLLECTION:
      case StatisticType.TIME_FROM_COLLECTION_TO_DELIVERY:
      case StatisticType.AVERAGE_PROCESSING_TIME:
        return 'hours';
      case StatisticType.FULL_PERCENTAGE:
      case StatisticType.WASTE_DIVERSION_RATE:
      case StatisticType.QUALITY_COMPLIANCE_RATE:
      case StatisticType.REJECTED_WASTE_PERCENTAGE:
      case StatisticType.PROCESSING_CAPACITY_UTILIZATION:
      case StatisticType.MAINTENANCE_SCHEDULE_COMPLIANCE:
      case StatisticType.DRIVER_PERFORMANCE_SCORE:
      case StatisticType.CUSTOMER_SATISFACTION_SCORE:
      case StatisticType.WASTE_COMPACTION_EFFICIENCY:
      case StatisticType.STORAGE_CAPACITY_UTILIZATION:
      case StatisticType.WASTE_QUALITY_RATING:
      case StatisticType.AVERAGE_CONTAINER_FILL_RATE:
        return '%';
      case StatisticType.RECYCLER_APPETITE:
      case StatisticType.SUSTAINABLE_SUPPLY:
      case StatisticType.FUEL_CONSUMPTION_PER_TRIP:
        return 'kg/day';
      case StatisticType.AVERAGE_INCENTIVE_PER_KG:
        return 'SAR/kg';
      case StatisticType.TOTAL_REVENUE:
      case StatisticType.TOTAL_REVENUE_LAST_MONTH:
        return 'SAR';
      case StatisticType.COLLECTION_FREQUENCY:
        return 'times/day';
      case StatisticType.AVERAGE_ROUTE_EFFICIENCY:
        return 'km/L';
      case StatisticType.TRIPS_TODAY_COUNT:
      case StatisticType.EMERGENCY_COLLECTION_REQUESTS:
        return 'trips';
      case StatisticType.TRUCK_DRIVER_COUNT:
        return 'drivers';
      default:
        return '';
    }
  };


  const renderStatCard = (
    stat: StatisticType,
    operational: DashboardStatistics | null | undefined
  ) => {
    if (!operational) return null;
    const key = stat as keyof DashboardStatistics;
    if (operational[key] === undefined || operational[key] === null) return null;

    const data = operational[key] as unknown;
    const chartType = chartTypes[stat] || 'bar';

    // Handle array data statistics
    if (Array.isArray(data)) {
      // Handle supply per recycler data
      if (stat === StatisticType.SUPPLY_PER_RECYCLER) {
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            data={data}
            type={chartType}
            dataKey="supply_amount"
            nameKey="recycler_name"
            onMenuClick={(e) => handleMenuOpen(e, stat)}
          />
        );
      }
      // Handle incoming waste breakdown
      if (stat === StatisticType.INCOMING_WASTE_BREAKDOWN) {
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            data={data}
            type={chartType}
            dataKey="amount"
            nameKey="date"
            chartMinHeight={400}
            fillPaper
            onMenuClick={(e) => handleMenuOpen(e, stat)}
          />
        );
      }
      // Handle waste type breakdown - transform waste_type_material_breakdown to simple format for pie chart
      if (stat === StatisticType.WASTE_TYPE_BREAKDOWN) {
        const transformedData = (data as any[]).map((item) => ({
          waste_type: item.waste_type,
          total_kg: item.total_kg,
        }));
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            data={transformedData}
            type="pie"
            dataKey="total_kg"
            nameKey="waste_type"
            onMenuClick={(e) => handleMenuOpen(e, stat)}
          />
        );
      }
    }

    // Handle single value statistics
    if (typeof data === 'number') {
      const currentValue = data;
      const previousKey = `previous_month_${stat}` as keyof DashboardStatistics;
      const rawPrev = operational[previousKey];
      const previousValue =
        typeof rawPrev === 'number' ? rawPrev : undefined;
      const change =
        previousValue === undefined || previousValue === 0
          ? 0
          : ((currentValue - previousValue) / previousValue) * 100;

      return (
        <StatCard
          title={getDisplayTitle(stat)}
          value={currentValue}
          unit={getUnitForStat(stat)}
          trend={change}
          onMenuClick={(e) => handleMenuOpen(e, stat)}
          icon={
            stat === StatisticType.AVERAGE_INCENTIVE_PER_KG ? (
              <PaymentsIcon />
            ) : undefined
          }
        />
      );
    }

    return null;
  };

  const renderEsgMonthlyMetric = (metric: EsgMonthlyMetricType) => {
    if (!esgMonthly) {
      return esgLoading ? (
        <Box
          key={metric}
          sx={{
            minHeight: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '16px',
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.loadingEsg')}
          </Typography>
        </Box>
      ) : null;
    }

    const title = getEsgMetricTitle(metric);
    const mom = esgMonthlyMetricMoM(esgMonthly, metric);
    const fmtSar = (n: number) =>
      n.toLocaleString('en-US', { style: 'currency', currency: 'SAR' });
    const costLabel = (amount: number) =>
      `${fmtSar(amount)} ${t('units.cost')}`;

    switch (metric) {
      case EsgMonthlyMetricType.WASTE_DIVERSION_RATE:
        return (
          <StatCard
            title={title}
            icon={<PercentIcon />}
            value={esgMonthly.waste_diversion_rate_percent ?? 0}
            unit="%"
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            subtitle={t('dashboard.thisMonth')}
            hideMenu
            onMenuClick={() => {}}
          />
        );
      case EsgMonthlyMetricType.TOTAL_CO2: {
        const tco2e = pickTotalAvoidedTco2e(esgMonthly);
        const uncounted = uncountedPickupsNote(esgMonthly, t);
        const card = (
          <StatCard
            title={title}
            icon={<Co2Icon />}
            value={tco2e}
            unit={t('units.tco2e')}
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            subtitle={t('dashboard.basedOnDeliveryDate')}
            hideMenu
            onMenuClick={() => {}}
          />
        );
        if (uncounted) {
          return (
            <Tooltip title={uncounted}>
              <Box sx={{ height: '100%' }}>{card}</Box>
            </Tooltip>
          );
        }
        return card;
      }
      case EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED:
        return (
          <StatCard
            title={title}
            icon={<RecyclingIcon />}
            value={esgMonthly.total_waste_diverted_kg ?? 0}
            unit={t('units.kg')}
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            subtitle={t('dashboard.thisMonth')}
            hideMenu
            onMenuClick={() => {}}
          />
        );
      case EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT:
        return (
          <StatCard
            key={metric}
            title={title}
            icon={<AssessmentIcon />}
            value={esgMonthly.fill_level_report_count}
            unit={t('units.reports')}
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            hideMenu
            onMenuClick={() => {}}
          />
        );
      case EsgMonthlyMetricType.TRAINING_HOURS_TOTAL:
        if (esgMonthly.training_hours_total === null) {
          return (
            <StatCard
              key={metric}
              title={title}
              icon={<SchoolIcon />}
              value={null}
              unit={t('units.hours')}
              trend={0}
              hideTrend
              subtitle={t('dashboard.alerts.trainingNotAvailable')}
              hideMenu
              onMenuClick={() => {}}
            />
          );
        }
        return (
          <StatCard
            key={metric}
            title={title}
            icon={<SchoolIcon />}
            value={esgMonthly.training_hours_total}
            unit={t('units.hours')}
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            hideMenu
            onMenuClick={() => {}}
          />
        );
      case EsgMonthlyMetricType.WATER_USE:
        return (
          <StatCard
            key={metric}
            title={title}
            icon={<WaterDropIcon />}
            value={esgMonthly.water_consumption}
            unit={t('units.units')}
            subValue={costLabel(esgMonthly.water_amount_sar)}
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            hideMenu
            onMenuClick={() => {}}
          />
        );
      case EsgMonthlyMetricType.ELECTRICITY_USE:
        return (
          <StatCard
            key={metric}
            title={title}
            icon={<BoltIcon />}
            value={esgMonthly.electricity_consumption_kwh}
            unit={t('units.kwh')}
            subValue={costLabel(esgMonthly.electricity_amount_sar)}
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            hideMenu
            onMenuClick={() => {}}
          />
        );
      case EsgMonthlyMetricType.OIL_USE:
        return (
          <StatCard
            key={metric}
            title={title}
            icon={<LocalFireDepartmentIcon />}
            value={esgMonthly.oil_consumption_liters}
            unit={t('units.liters')}
            subValue={costLabel(esgMonthly.oil_amount_sar)}
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            hideMenu
            onMenuClick={() => {}}
          />
        );
      case EsgMonthlyMetricType.GAS_USE:
        return (
          <StatCard
            key={metric}
            title={title}
            icon={<LocalGasStationIcon />}
            value={esgMonthly.gas_consumption_m3}
            unit={t('units.m3')}
            subValue={costLabel(esgMonthly.gas_amount_sar)}
            trend={mom.trend}
            hideTrend={mom.hideTrend}
            hideMenu
            onMenuClick={() => {}}
          />
        );
      case EsgMonthlyMetricType.RECYCLED_WASTE_BREAKDOWN: {
        const wasteTypeMaterialRows = esgMonthly.waste_type_material_breakdown ?? [];
        if (!wasteTypeMaterialRows.length) {
          return (
            <Paper
              key={metric}
              sx={{
                p: 3,
                borderRadius: '16px',
                boxShadow: theme.shadows[2],
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.alerts.noWasteBreakdown')}
              </Typography>
            </Paper>
          );
        }
        const chartType = chartTypes[metric] || 'pie';
        return (
          <EsgRecycledWasteByTypeChart
            key={metric}
            rows={wasteTypeMaterialRows}
            title={t('metrics.waste_type_material')}
            chartType={chartType}
            onMenuClick={(e) => handleMenuOpen(e, metric)}
          />
        );
      }
      case EsgMonthlyMetricType.CARBON_AVOIDED_BY_STREAM: {
        const chartType = chartTypes[metric] || 'pie';
        return (
          <CarbonAvoidedByStreamChart
            key={metric}
            streams={esgMonthly.avoided_tco2e_by_stream ?? []}
            title={t('metrics.carbon_avoidance_stream')}
            chartType={chartType}
            onMenuClick={(e) => handleMenuOpen(e, metric)}
          />
        );
      }
      case EsgMonthlyMetricType.CARBON_AVOIDED_BY_MATERIAL:
        return (
          <CarbonAvoidedByMaterialTable
            key={metric}
            rows={esgMonthly.avoided_tco2e_by_material ?? []}
            title={title}
          />
        );
      default:
        return null;
    }
  };

  if (!selectedOrganization?.id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <Typography>{t('dashboard.selectOrganization')}</Typography>
      </Box>
    );
  }

  const formatSar = (amount: number) =>
    amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'SAR',
      maximumFractionDigits: 2,
    });

  const skeletonVariant =
    dashboardTab === DashboardSection.MONTHLY ||
    dashboardTab === DashboardSection.SCOPES
      ? 'monthly'
      : dashboardTab === DashboardSection.OVERVIEW
        ? 'overview'
        : dashboardTab === DashboardSection.BRANCHES
          ? 'branches'
          : 'carbon';

  const isTabLoading =
    (esgLoading && dashboardTab !== DashboardSection.CARBON_AVOIDANCE) ||
    (carbonLoading && dashboardTab === DashboardSection.CARBON_AVOIDANCE);

  return (
    <Box>
      {esgError && dashboardTab !== DashboardSection.CARBON_AVOIDANCE && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('dashboard.alerts.esgLoadError', { error: esgError })}
        </Alert>
      )}
      {carbonError && dashboardTab === DashboardSection.CARBON_AVOIDANCE && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('dashboard.alerts.carbonLoadError', { error: carbonError })}
        </Alert>
      )}
      <PageHeader
        icon={<DashboardIcon />}
        title={t('dashboard.title')}
        actions={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            {(dashboardTab === DashboardSection.MONTHLY ||
              dashboardTab === DashboardSection.SCOPES ||
              dashboardTab === DashboardSection.BRANCHES) && (
              <SetupCoachmark
                open={showMonthTip}
                title={t('selfSetup.tour.dashboardMonthTitle')}
                subtitle={t('selfSetup.tour.dashboardMonthSubtitle')}
                onDismiss={softDismissDashboardTour}
                onNext={advanceDashboardGuide}
                nextLabel={t('selfSetup.tourNext')}
              >
                <Box id="self-setup-dashboard-month">
                  <DatePicker
                    views={['year', 'month']}
                    openTo="month"
                    value={selectedMonthDate}
                    maxDate={startOfMonth(new Date())}
                    onChange={(newValue) => {
                      if (newValue && isValid(newValue)) {
                        const start = startOfMonth(newValue);
                        handleMonthChange(format(start, "yyyy-MM-dd'T'HH:mm:ss'Z'"));
                      }
                    }}
                    format="MMMM yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 220,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: theme.palette.background.default,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </SetupCoachmark>
            )}
            {dashboardTab === DashboardSection.BRANCHES && (
              <SetupCoachmark
                open={showCompareTip}
                title={t('selfSetup.tour.dashboardCompareTitle')}
                subtitle={t('selfSetup.tour.dashboardCompareSubtitle')}
                onDismiss={softDismissDashboardTour}
                onNext={advanceDashboardGuide}
                nextLabel={t('selfSetup.tourNext')}
              >
                <Button
                  id="self-setup-dashboard-compare"
                  variant="outlined"
                  startIcon={<CompareArrowsIcon />}
                  onClick={() => {
                    setOpenBranchCompare(true);
                    if (showCompareTip) {
                      syncPhase('compareDialog');
                    }
                  }}
                  disabled={
                    !(showCompareTip || showCompareTourDialog) &&
                    (esgBranchRows.length < 2 || esgLoading)
                  }
                  sx={{ borderRadius: '12px' }}
                >
                  {t('dashboard.branch.compare.button')}
                </Button>
              </SetupCoachmark>
            )}
            <SetupCoachmark
              open={showKpiTip}
              title={t('selfSetup.tour.dashboardKpiTitle')}
              subtitle={t('selfSetup.tour.dashboardKpiSubtitle')}
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardGuide}
              nextLabel={t('selfSetup.tourNext')}
            >
              <Button
                id="self-setup-dashboard-kpi"
                variant="text"
                startIcon={<SettingsIcon />}
                onClick={handleOpenKPIDialog}
                sx={{ borderRadius: '12px' }}
              >
                {t('dashboard.kpiLimits')}
              </Button>
            </SetupCoachmark>
            <SetupCoachmark
              open={showCustomizeButtonTip}
              title={
                helpCustomizeGuide
                  ? tHelp('knowledgeBase.customizeGuideTitle')
                  : t('selfSetup.tour.dashboardTitle')
              }
              subtitle={
                helpCustomizeGuide
                  ? tHelp('knowledgeBase.customizeGuideSubtitle')
                  : t('selfSetup.tour.dashboardSubtitle')
              }
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardGuide}
              nextLabel={t('selfSetup.tourNext')}
            >
              <Button
                id="self-setup-dashboard-customize"
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={handleOpenDialog}
                sx={{
                  borderRadius: '12px',
                  px: 2.25,
                  py: 1,
                  minHeight: 40,
                  textTransform: 'none',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  gap: 0.75,
                }}
              >
                {t('dashboard.customize')}
              </Button>
            </SetupCoachmark>
          </Box>
        }
      >
        <Box sx={PAGE_HEADER_TABS_ROW_SX}>
            <Tabs
              id="self-setup-dashboard-tabs"
              value={dashboardTab}
              onChange={(_, value: DashboardSection) => setDashboardTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={PAGE_HEADER_TABS_SX}
            >
              <Tab
                label={
                  showMonthlyTabTip ? (
                    <SetupCoachmark
                      open
                      compact
                      title={t('selfSetup.tour.dashboardTabsTitle')}
                      subtitle={t('selfSetup.tour.dashboardTabsSubtitle')}
                      onDismiss={softDismissDashboardTour}
                      onNext={advanceDashboardGuide}
                      nextLabel={t('selfSetup.tourNext')}
                    >
                      <Box
                        id="self-setup-dashboard-tab-monthly"
                        component="span"
                        sx={{ display: 'inline-block' }}
                      >
                        {t('dashboard.tabs.monthly')}
                      </Box>
                    </SetupCoachmark>
                  ) : (
                    t('dashboard.tabs.monthly')
                  )
                }
              />
              <Tab label={t('dashboard.tabs.overview')} />
              <Tab label={t('dashboard.tabs.scopes')} />
              <Tab
                label={
                  showBranchesTabTip ? (
                    <SetupCoachmark
                      open
                      compact
                      title={t('selfSetup.tour.dashboardBranchesTabTitle')}
                      subtitle={t('selfSetup.tour.dashboardBranchesTabSubtitle')}
                      onDismiss={softDismissDashboardTour}
                      onNext={advanceDashboardGuide}
                      nextLabel={t('selfSetup.tourNext')}
                    >
                      <Box
                        id="self-setup-dashboard-tab-branches"
                        component="span"
                        sx={{ display: 'inline-block' }}
                      >
                        {t('dashboard.tabs.branches')}
                      </Box>
                    </SetupCoachmark>
                  ) : (
                    t('dashboard.tabs.branches')
                  )
                }
              />
              <Tab
                label={
                  showCarbonTabTip ? (
                    <SetupCoachmark
                      open
                      compact
                      title={t('selfSetup.tour.dashboardCarbonTabTitle')}
                      subtitle={t('selfSetup.tour.dashboardCarbonTabSubtitle')}
                      onDismiss={softDismissDashboardTour}
                      onNext={advanceDashboardGuide}
                      nextLabel={t('selfSetup.tourNext')}
                    >
                      <Box
                        id="self-setup-dashboard-tab-carbon"
                        component="span"
                        sx={{ display: 'inline-block' }}
                      >
                        {t('dashboard.tabs.carbonAvoidance')}
                      </Box>
                    </SetupCoachmark>
                  ) : (
                    t('dashboard.tabs.carbonAvoidance')
                  )
                }
              />
            </Tabs>
          </Box>
      </PageHeader>

      {isTabLoading && <DashboardSkeleton variant={skeletonVariant} />}

      {!isTabLoading && dashboardTab === DashboardSection.MONTHLY && (
        <Box sx={{ mb: 3 }}>
          {esgMonthly && hasEnabledKPIs && selectedMonthlyExtras.includes('kpi_progress') && (
            <Box
              sx={{
                mb: 4,
                p: 2,
                borderRadius: '12px',
                backgroundColor: theme.palette.action.hover,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                {t('dashboard.sections.kpiProgress')}
              </Typography>
              <Grid container spacing={1.5}>
                {Object.entries(esgMonthly)
                  .filter(
                    ([key, value]) =>
                      typeof value === 'number' &&
                      !key.startsWith('previous_month_') &&
                      !ESG_MONTHLY_NON_OPERATIONAL_KEYS.has(key) &&
                      kpiLimitedKeys.includes(key)
                  )
                  .sort(
                    (a, b) =>
                      Number((a[1] as number) === 0) - Number((b[1] as number) === 0)
                  )
                  .map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={key}>
                      <KPIProgressCard
                        title={getDisplayTitle(key)}
                        value={value as number}
                        unit={KPI_UNITS[key] ?? ''}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}

          {esgMonthly &&
            (selectedMonthlyExtras.includes('scope_1') ||
              selectedMonthlyExtras.includes('scope_2')) && (
            <ScopeEmissionsPanel
              summary={esgMonthly.scope_emissions}
              showBreakdown={false}
              showScope1={selectedMonthlyExtras.includes('scope_1')}
              showScope2={selectedMonthlyExtras.includes('scope_2')}
            />
          )}
          <SetupCoachmark
            open={showEsgCardsTip}
            title={t('selfSetup.tour.dashboardEsgTitle')}
            subtitle={t('selfSetup.tour.dashboardEsgSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
          >
            <Box id="self-setup-dashboard-esg">
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                ESG
              </Typography>
              {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Monthly ESG cards
          </Typography> */}
              {(() => {
                const isTopRow = (item: (typeof orderedMonthlyNumberCards)[number]) =>
                  item.kind === 'esg' &&
                  (item.metric === EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED ||
                    item.metric === EsgMonthlyMetricType.WASTE_DIVERSION_RATE ||
                    item.metric === EsgMonthlyMetricType.TOTAL_CO2);

                const isSecondRow = (item: (typeof orderedMonthlyNumberCards)[number]) =>
                  item.kind === 'esg' &&
                  (item.metric === EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT ||
                    item.metric === EsgMonthlyMetricType.TRAINING_HOURS_TOTAL);

                const topRow = orderedMonthlyNumberCards.filter(isTopRow);
                const secondRow = orderedMonthlyNumberCards.filter(isSecondRow);
                const rest = orderedMonthlyNumberCards.filter((i) => !isTopRow(i) && !isSecondRow(i));

                return (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr)',
                          sm: 'repeat(2, minmax(0, 1fr))',
                          md: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      {topRow.map((item) =>
                        item.kind === 'esg' ? (
                          <Box key={item.metric}>{renderEsgMonthlyMetric(item.metric)}</Box>
                        ) : (
                          <Box key={item.stat}>{renderStatCard(item.stat, operationalDashboard)}</Box>
                        )
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr)',
                          md: 'repeat(2, minmax(0, 1fr))',
                        },
                        gap: 2,
                        mb: rest.length > 0 ? 2 : hasNoOperationalData ? 2 : 3,
                      }}
                    >
                      {secondRow.map((item) =>
                        item.kind === 'esg' ? (
                          <Box key={item.metric}>{renderEsgMonthlyMetric(item.metric)}</Box>
                        ) : (
                          <Box key={item.stat}>{renderStatCard(item.stat, operationalDashboard)}</Box>
                        )
                      )}
                    </Box>

                    {rest.length > 0 && (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'minmax(0, 1fr)',
                            sm: 'repeat(auto-fit, minmax(280px, 1fr))',
                          },
                          gap: 2,
                          mb: hasNoOperationalData ? 2 : 3,
                        }}
                      >
                        {rest.map((item) =>
                          item.kind === 'esg' ? (
                            <Box key={item.metric}>{renderEsgMonthlyMetric(item.metric)}</Box>
                          ) : (
                            <Box key={item.stat}>{renderStatCard(item.stat, operationalDashboard)}</Box>
                          )
                        )}
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>
          </SetupCoachmark>
          {hasNoOperationalData && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('dashboard.alerts.noOperationalDataMonthly')}
            </Alert>
          )}

          <SetupCoachmark
            open={showChartsTip}
            title={t('selfSetup.tour.dashboardChartsTitle')}
            subtitle={t('selfSetup.tour.dashboardChartsSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
          >
            <Box id="self-setup-dashboard-charts">
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                {t('dashboard.sections.charts')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('dashboard.chartsHint')}
              </Typography>
              {hasMonthlyCharts ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      sm: 'repeat(2, minmax(0, 1fr))',
                    },
                    gap: 3,
                    mb: 2,
                    alignItems: 'stretch',
                  }}
                >
                  {esgMonthlyChartMetrics.map((m) => (
                    <Box key={m} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      {renderEsgMonthlyMetric(m)}
                    </Box>
                  ))}
                  {operationalChartStats.map((stat) => (
                    <Box
                      key={stat}
                      sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
                    >
                      {renderStatCard(stat, operationalDashboard)}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('dashboard.alerts.noCharts')}
                </Alert>
              )}
            </Box>
          </SetupCoachmark>
        </Box>
      )}

      {!isTabLoading && dashboardTab === DashboardSection.OVERVIEW && (
        <Box sx={{ mb: 3 }}>
          {/* <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Cumulative figures
          </Typography> */}
          {!esgOverview && !esgError && (
            <Typography color="text.secondary">{t('dashboard.alerts.noOverview')}</Typography>
          )}
          {esgOverview && (
            <>
              {(selectedOverviewSet.has('scope_1') ||
                selectedOverviewSet.has('scope_2')) && (
                <ScopeEmissionsPanel
                  summary={esgOverview.scope_emissions}
                  showBreakdown={false}
                  showScope1={selectedOverviewSet.has('scope_1')}
                  showScope2={selectedOverviewSet.has('scope_2')}
                />
              )}
              {selectedOverviewWidgets.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('dashboard.alerts.noOverviewWidgets')}
                </Alert>
              )}

              {OVERVIEW_SUMMARY_WIDGETS.some((id) => selectedOverviewSet.has(id)) && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('dashboard.sections.wasteDiversion')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        md: 'repeat(3, minmax(0, 1fr))',
                      },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    {selectedOverviewSet.has('summary_waste_diverted') && (
                      <StatCard
                        title={t('dashboard.overviewWidgets.summary_waste_diverted')}
                        icon={<RecyclingIcon />}
                        value={esgOverview.total_waste_diverted_kg ?? 0}
                        unit={t('units.kg')}
                        trend={0}
                        hideTrend
                        subtitle={t('dashboard.allTime')}
                        hideMenu
                        onMenuClick={() => {}}
                      />
                    )}
                    {selectedOverviewSet.has('summary_diversion_rate') && (
                      <StatCard
                        title={t('dashboard.overviewWidgets.summary_diversion_rate')}
                        icon={<PercentIcon />}
                        value={esgOverview.waste_diversion_rate_percent ?? 0}
                        unit="%"
                        trend={0}
                        hideTrend
                        subtitle={t('dashboard.allTime')}
                        hideMenu
                        onMenuClick={() => {}}
                      />
                    )}
                    {selectedOverviewSet.has('summary_total_co2') &&
                      (() => {
                        const tco2e = pickTotalAvoidedTco2e(esgOverview);
                        const uncounted = uncountedPickupsNote(esgOverview, t);
                        const card = (
                          <StatCard
                            title={t('dashboard.overviewWidgets.summary_total_co2')}
                            icon={<Co2Icon />}
                            value={tco2e}
                            unit={t('units.tco2e')}
                            trend={0}
                            hideTrend
                            subtitle={t('dashboard.allTimeDeliveryNote')}
                            hideMenu
                            onMenuClick={() => {}}
                          />
                        );
                        if (uncounted) {
                          return (
                            <Tooltip title={uncounted}>
                              <Box sx={{ height: '100%' }}>{card}</Box>
                            </Tooltip>
                          );
                        }
                        return card;
                      })()}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        md: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 2,
                      mb: 4,
                    }}
                  >
                    {selectedOverviewSet.has('summary_fill_reports') && (
                      <StatCard
                        title={t('dashboard.overviewWidgets.summary_fill_reports')}
                        icon={<AssessmentIcon sx={{ fontSize: 32 }} />}
                        value={esgOverview.fill_level_report_count}
                        unit={t('units.reports')}
                        trend={0}
                        hideTrend
                        onMenuClick={() => {}}
                      />
                    )}
                    {selectedOverviewSet.has('summary_training_hours') &&
                      (esgOverview.training_hours_total !== null ? (
                        <StatCard
                          title={t('dashboard.overviewWidgets.summary_training_hours')}
                          icon={<SchoolIcon sx={{ fontSize: 32 }} />}
                          value={esgOverview.training_hours_total}
                          unit={t('units.hours')}
                          trend={0}
                          hideTrend
                          onMenuClick={() => {}}
                        />
                      ) : (
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: '16px',
                            boxShadow: theme.shadows[2],
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            {t('dashboard.trainingHoursAllTime')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('dashboard.alerts.trainingNotAvailable')}
                          </Typography>
                        </Paper>
                      ))}
                  </Box>
                </>
              )}

              {OVERVIEW_UTILITY_WIDGETS.some((id) => selectedOverviewSet.has(id)) && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('dashboard.utilitiesSection')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(4, 1fr)',
                      },
                      gap: 2,
                      mb: 4,
                    }}
                  >
                    {selectedOverviewSet.has('utility_water') && (
                      <StatCard
                        title={t('dashboard.overviewWidgets.utility_water')}
                        icon={<WaterDropIcon sx={{ fontSize: 32 }} />}
                        value={esgOverview.water_consumption}
                        unit={t('units.units')}
                        subValue={`${formatOverviewSar(esgOverview.water_amount_sar)} ${t('units.cost')}`}
                        trend={0}
                        hideTrend
                        onMenuClick={() => {}}
                      />
                    )}
                    {selectedOverviewSet.has('utility_electricity') && (
                      <StatCard
                        title={t('dashboard.overviewWidgets.utility_electricity')}
                        icon={<BoltIcon sx={{ fontSize: 32 }} />}
                        value={esgOverview.electricity_consumption_kwh}
                        unit={t('units.kwh')}
                        subValue={`${formatOverviewSar(esgOverview.electricity_amount_sar)} ${t('units.cost')}`}
                        trend={0}
                        hideTrend
                        onMenuClick={() => {}}
                      />
                    )}
                    {selectedOverviewSet.has('utility_oil') && (
                      <StatCard
                        title={t('dashboard.overviewWidgets.utility_oil')}
                        icon={<LocalFireDepartmentIcon sx={{ fontSize: 32 }} />}
                        value={esgOverview.oil_consumption_liters}
                        unit={t('units.liters')}
                        subValue={`${formatOverviewSar(esgOverview.oil_amount_sar)} ${t('units.cost')}`}
                        trend={0}
                        hideTrend
                        onMenuClick={() => {}}
                      />
                    )}
                    {selectedOverviewSet.has('utility_gas') && (
                      <StatCard
                        title={t('dashboard.overviewWidgets.utility_gas')}
                        icon={<LocalGasStationIcon sx={{ fontSize: 32 }} />}
                        value={esgOverview.gas_consumption_m3}
                        unit={t('units.m3')}
                        subValue={`${formatOverviewSar(esgOverview.gas_amount_sar)} ${t('units.cost')}`}
                        trend={0}
                        hideTrend
                        onMenuClick={() => {}}
                      />
                    )}
                  </Box>
                </>
              )}

              {((selectedOverviewSet.has('chart_recycled') &&
                (esgOverview.waste_type_material_breakdown?.length ?? 0) > 0) ||
                (selectedOverviewSet.has('chart_incoming') &&
                  Array.isArray(esgOverview.incoming_waste_breakdown) &&
                  esgOverview.incoming_waste_breakdown.length > 0) ||
                selectedOverviewSet.has('chart_carbon_stream') ||
                selectedOverviewSet.has('chart_carbon_material')) && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('dashboard.sections.charts')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2,
                      mb: 4,
                      alignItems: 'stretch',
                    }}
                  >
                    {selectedOverviewSet.has('chart_recycled') &&
                      (esgOverview.waste_type_material_breakdown?.length ?? 0) > 0 && (
                        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <EsgRecycledWasteByTypeChart
                            rows={esgOverview.waste_type_material_breakdown}
                            title={t('metrics.waste_type_material')}
                            chartType={chartTypes.esg_overview_breakdown || 'pie'}
                            onMenuClick={(e) => handleMenuOpen(e, 'esg_overview_breakdown')}
                          />
                        </Box>
                      )}
                    {selectedOverviewSet.has('chart_incoming') &&
                      Array.isArray(esgOverview.incoming_waste_breakdown) &&
                      esgOverview.incoming_waste_breakdown.length > 0 && (
                        <Box
                          sx={{
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                          }}
                        >
                          {renderStatCard(
                            StatisticType.INCOMING_WASTE_BREAKDOWN,
                            esgOverview
                          )}
                        </Box>
                      )}
                    {selectedOverviewSet.has('chart_carbon_stream') && (
                      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <CarbonAvoidedByStreamChart
                          streams={esgOverview.avoided_tco2e_by_stream ?? []}
                          title={t('metrics.carbon_avoidance_stream')}
                          chartType={chartTypes.esg_overview_carbon_stream || 'pie'}
                          onMenuClick={(e) => handleMenuOpen(e, 'esg_overview_carbon_stream')}
                        />
                      </Box>
                    )}
                    {selectedOverviewSet.has('chart_carbon_material') && (
                      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <CarbonAvoidedByMaterialTable
                          rows={esgOverview.avoided_tco2e_by_material ?? []}
                          title={t('metrics.carbon_avoidance_material')}
                        />
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      )}

      {!isTabLoading && dashboardTab === DashboardSection.SCOPES && (
        <Box sx={{ mb: 3 }}>
          {esgMonthly ? (
            selectedScopesWidgets.length === 0 ? (
              <Alert severity="info">{t('dashboard.alerts.noTabWidgets')}</Alert>
            ) : (
              <ScopeEmissionsPanel
                summary={esgMonthly.scope_emissions}
                showBreakdown={selectedScopesWidgets.includes('breakdown')}
                showScope1={selectedScopesWidgets.includes('scope_1')}
                showScope2={selectedScopesWidgets.includes('scope_2')}
              />
            )
          ) : (
            <Alert severity="info">No scope emissions data for this reporting month.</Alert>
          )}
        </Box>
      )}

      {!isTabLoading && dashboardTab === DashboardSection.BRANCHES && (
        <Box sx={{ mb: 3 }}>
          <SetupCoachmark
            open={showBranchesTip}
            title={t('selfSetup.tour.dashboardBranchesTitle')}
            subtitle={t('selfSetup.tour.dashboardBranchesSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
            placement="top"
          >
            <Box id="self-setup-dashboard-branches">
              {esgByBranch && esgBranchRows.length === 0 && (
                <Typography color="text.secondary">{t('dashboard.alerts.noBranchData')}</Typography>
              )}
              {esgBranchRows.length > 0 && selectedBranchesWidgets.length === 0 && (
                <Alert severity="info">{t('dashboard.alerts.noTabWidgets')}</Alert>
              )}
              {esgBranchRows.length > 0 && selectedBranchesWidgets.length > 0 && (
                <Grid container spacing={2}>
                  {esgBranchRows.map((row) => (
                    <Grid item xs={12} md={6} key={row.collection_site_id}>
                      <BranchSiteCard
                        row={row}
                        orgTrainingHours={esgByBranch?.training_hours_total}
                        formatSar={formatSar}
                        visibleWidgets={selectedBranchesWidgets}
                        highlightHole={showBranchesTip}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </SetupCoachmark>
        </Box>
      )}

      {!isTabLoading && dashboardTab === DashboardSection.CARBON_AVOIDANCE && (
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={2}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: '16px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <SetupCoachmark
              open={showCarbonDateTip}
              title={t('selfSetup.tour.dashboardCarbonDateTitle')}
              subtitle={t('selfSetup.tour.dashboardCarbonDateSubtitle')}
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardGuide}
              nextLabel={t('selfSetup.tourNext')}
            >
              <Box
                id="self-setup-dashboard-carbon-date"
                sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}
              >
                <DatePicker
                  label={t('dashboard.startDate')}
                  value={carbonPeriod.startDate}
                  maxDate={carbonPeriod.endDate}
                  onChange={(newValue) => {
                    if (newValue && isValid(newValue)) {
                      updateCarbonPeriod({ startDate: startOfDay(newValue) });
                    }
                  }}
                  format="MMM d, yyyy"
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                />
                <DatePicker
                  label={t('dashboard.endDate')}
                  value={carbonPeriod.endDate}
                  minDate={carbonPeriod.startDate}
                  maxDate={carbonToday}
                  onChange={(newValue) => {
                    if (newValue && isValid(newValue)) {
                      updateCarbonPeriod({ endDate: startOfDay(newValue) });
                    }
                  }}
                  format="MMM d, yyyy"
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                />
              </Box>
            </SetupCoachmark>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {carbonPeriodLabel}
            </Typography>
          </Paper>

          {!carbonPeriodValid && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('dashboard.alerts.carbonDateWarning')}
            </Alert>
          )}

          <SetupCoachmark
            open={showCarbonContentTip}
            title={t('selfSetup.tour.dashboardCarbonContentTitle')}
            subtitle={t('selfSetup.tour.dashboardCarbonContentSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourDone')}
            fullWidth
          >
            <Box id="self-setup-dashboard-carbon-content">
              {carbonReport && carbonPeriodValid && !carbonLoading && (
                selectedCarbonWidgets.length === 0 ? (
                  <Alert severity="info">{t('dashboard.alerts.noTabWidgets')}</Alert>
                ) : (
                  <CarbonAvoidanceDetailView
                    report={carbonReport}
                    periodLabel={carbonPeriodLabel}
                    visibleWidgets={selectedCarbonWidgets}
                  />
                )
              )}

              {!carbonReport && !carbonLoading && carbonPeriodValid && !carbonError && (
                <Typography color="text.secondary">{t('dashboard.alerts.noCarbonData')}</Typography>
              )}
            </Box>
          </SetupCoachmark>
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            borderRadius: "12px",
          }
        }}
      >
        <MenuItem onClick={() => handleChartTypeChange('bar')}>
          <ListItemIcon>
            <BarChartIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          </ListItemIcon>
          <ListItemText>{t('dashboard.chartTypes.bar')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange('line')}>
          <ListItemIcon>
            <LineChartIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
          </ListItemIcon>
          <ListItemText>{t('dashboard.chartTypes.line')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange('area')}>
          <ListItemIcon>
            <AreaChartIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
          </ListItemIcon>
          <ListItemText>{t('dashboard.chartTypes.area')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange('pie')}>
          <ListItemIcon>
            <PieChartIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
          </ListItemIcon>
          <ListItemText>{t('dashboard.chartTypes.pie')}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        hideBackdrop={
          showCustomizeSearchTip ||
          showCustomizeMetricsTip ||
          showCustomizeSaveTip
        }
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.06)} 0%, transparent 100%)`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.15rem',
                  lineHeight: 1.3,
                  color: 'text.primary',
                }}
              >
                {customizeDialogCopy.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.75,
                  color: 'text.secondary',
                  lineHeight: 1.55,
                  fontWeight: 400,
                  maxWidth: 440,
                }}
              >
                {customizeDialogCopy.hint}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('common.close')}
              onClick={handleCloseDialog}
              size="small"
              sx={{
                mt: -0.5,
                color: 'text.secondary',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.06)
                    : alpha(theme.palette.common.black, 0.04),
                '&:hover': {
                  color: 'text.primary',
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.1)
                      : alpha(theme.palette.common.black, 0.08),
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            maxHeight: '55vh',
            px: 3,
            pt: 2.5,
            pb: 2,
          }}
        >
          <Box id="help-customize-dialog">
          <Box sx={{ mb: 2 }}>
            <SetupCoachmark
              open={showCustomizeSearchTip}
              title={t('selfSetup.tour.dashboardSearchTitle')}
              subtitle={t('selfSetup.tour.dashboardSearchSubtitle')}
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardGuide}
              nextLabel={t('selfSetup.tourNext')}
              fullWidth
            >
              <TextField
                id="self-setup-dashboard-search"
                fullWidth
                size="small"
                placeholder={t('dashboard.searchWidgets')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Box sx={{ mr: 1, opacity: 0.6 }}><SearchIcon /></Box>,
                }}
              />
            </SetupCoachmark>
          </Box>

          <SetupCoachmark
            open={showCustomizeMetricsTip}
            title={t('selfSetup.tour.dashboardMetricsTitle')}
            subtitle={t('selfSetup.tour.dashboardMetricsSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
          >
            <Box id="self-setup-dashboard-metrics">
              {dashboardTab === DashboardSection.MONTHLY && (
                <DashboardCustomizeWidgetList
                  items={monthlyCustomizeItems}
                  selectedIds={monthlyCustomizeSelected}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllMetrics')}
                  onChange={(next) => {
                    const extras = ESG_MONTHLY_EXTRA_WIDGETS.filter((id) => next.includes(id));
                    const metrics = PICKABLE_ESG_MONTHLY_METRICS.filter((id) => next.includes(id));
                    const ops = operationalStatsForOrg.filter((id) => next.includes(id));
                    setSelectedMonthlyExtras(extras);
                    setSelectedEsgMonthlyMetrics(metrics);
                    setSelectedStats(ops);
                    sessionStorage.setItem('esgMonthlySelectedExtras', JSON.stringify(extras));
                    sessionStorage.setItem('esgMonthlySelectedMetrics', JSON.stringify(metrics));
                    if (orgType) persistOperationalStatsForOrg(orgType, ops);
                  }}
                />
              )}
              {dashboardTab === DashboardSection.OVERVIEW && (
                <DashboardCustomizeWidgetList
                  items={PICKABLE_ESG_OVERVIEW_WIDGETS.map((id) => ({
                    id,
                    label: getOverviewWidgetLabel(t, id),
                  }))}
                  selectedIds={selectedOverviewWidgets}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllOverviewWidgets')}
                  onChange={(next) => {
                    const widgets = PICKABLE_ESG_OVERVIEW_WIDGETS.filter((id) => next.includes(id));
                    setSelectedOverviewWidgets(widgets);
                    sessionStorage.setItem('esgOverviewSelectedWidgets', JSON.stringify(widgets));
                  }}
                />
              )}
              {dashboardTab === DashboardSection.SCOPES && (
                <DashboardCustomizeWidgetList
                  items={scopesCustomizeItems}
                  selectedIds={selectedScopesWidgets}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllMetrics')}
                  onChange={(next) => {
                    const widgets = ESG_SCOPES_WIDGETS.filter((id) => next.includes(id));
                    setSelectedScopesWidgets(widgets);
                    sessionStorage.setItem('esgScopesSelectedWidgets', JSON.stringify(widgets));
                  }}
                />
              )}
              {dashboardTab === DashboardSection.BRANCHES && (
                <DashboardCustomizeWidgetList
                  items={branchesCustomizeItems}
                  selectedIds={selectedBranchesWidgets}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllMetrics')}
                  onChange={(next) => {
                    const widgets = ESG_BRANCHES_WIDGETS.filter((id) => next.includes(id));
                    setSelectedBranchesWidgets(widgets);
                    sessionStorage.setItem('esgBranchesSelectedWidgets', JSON.stringify(widgets));
                  }}
                />
              )}
              {dashboardTab === DashboardSection.CARBON_AVOIDANCE && (
                <DashboardCustomizeWidgetList
                  items={carbonCustomizeItems}
                  selectedIds={selectedCarbonWidgets}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllMetrics')}
                  onChange={(next) => {
                    const widgets = ESG_CARBON_WIDGETS.filter((id) => next.includes(id));
                    setSelectedCarbonWidgets(widgets);
                    sessionStorage.setItem('esgCarbonSelectedWidgets', JSON.stringify(widgets));
                  }}
                />
              )}
            </Box>
          </SetupCoachmark>
          </Box>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={handleCloseDialog} color="inherit">
            {t('common.cancel')}
          </Button>
          <SetupCoachmark
            open={showCustomizeSaveTip}
            title={t('selfSetup.tour.dashboardSaveTitle')}
            subtitle={t('selfSetup.tour.dashboardSaveSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={
              showOverviewTour && overviewTourPhase === 'customizeSave'
                ? t('selfSetup.tourNext')
                : t('selfSetup.tourDone')
            }
          >
            <Button
              id="self-setup-dashboard-save"
              onClick={handleSavePreferences}
              variant="contained"
            >
              {t('dashboard.saveChanges')}
            </Button>
          </SetupCoachmark>
        </DialogActions>
      </Dialog>

      <KPILimitsDialog
        open={openKPIDialog || showKpiFormTip || showKpiListTip}
        onClose={() => {
          if (showKpiFormTip || showKpiListTip) {
            softDismissDashboardTour();
            return;
          }
          handleCloseKPIDialog();
        }}
        tourTip={showKpiFormTip ? 'kpiForm' : showKpiListTip ? 'kpiList' : null}
        onTourNext={advanceDashboardGuide}
        onTourDismiss={softDismissDashboardTour}
        tourNextLabel={
          helpDashGuide === 'kpi' && showKpiListTip
            ? t('selfSetup.tourDone')
            : t('selfSetup.tourNext')
        }
      />

      <BranchComparisonDialog
        open={openBranchCompare || showCompareTourDialog}
        onClose={() => {
          if (showCompareTourDialog) {
            softDismissDashboardTour();
            return;
          }
          setOpenBranchCompare(false);
        }}
        branches={esgBranchRows}
        tourTip={
          showCompareResultsTip
            ? 'results'
            : showCompareDialogTip
              ? 'select'
              : null
        }
        onTourNext={advanceDashboardGuide}
        onTourDismiss={softDismissDashboardTour}
        tourTitle={
          showCompareResultsTip
            ? t('selfSetup.tour.dashboardCompareResultsTitle')
            : t('selfSetup.tour.dashboardCompareDialogTitle')
        }
        tourSubtitle={
          showCompareResultsTip
            ? t('selfSetup.tour.dashboardCompareResultsSubtitle')
            : t('selfSetup.tour.dashboardCompareDialogSubtitle')
        }
        tourNextLabel={
          helpDashGuide === 'branches' && showCompareResultsTip
            ? t('selfSetup.tourDone')
            : t('selfSetup.tourNext')
        }
      />
    </Box>
  );
};

export default Dashboard;
